import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Apple, Pencil } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { EmptyState, SkeletonLoader, UnitsRender } from "@/components/system";
import { EditMealDialog } from "./EditMealDialog";

/**
 * Today's meals — minimal list rendered below the quick-log strip.
 * Reads `meal` rows where ts falls inside the user's current local day.
 *
 * Build Week 4 will replace this with a proper "Right Now" + DayPlan slot list
 * per SPEC 3.1.2 #6. For now: prove the round trip — log meal → see it.
 */

type MealRow = {
  id: string;
  ts: string;
  name: string;
  kcal: number;
  macros: { p?: number; c?: number; f?: number } | null;
};

export function TodaysMeals() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const [editingId, setEditingId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["today_meals", userId],
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async () => {
      if (!userId) return [] as MealRow[];
      const { startISO, endISO } = todayBounds();
      const { data, error } = await supabase
        .from("meal")
        .select("id, ts, name, kcal, macros")
        .eq("user_id", userId)
        .gte("ts", startISO)
        .lt("ts", endISO)
        .order("ts", { ascending: true });
      if (error) throw error;
      return (data ?? []) as MealRow[];
    },
  });

  if (q.isLoading) {
    return <SkeletonLoader variant="block" count={2} />;
  }

  if (q.isError) {
    return (
      <p
        role="alert"
        style={{ color: "var(--brick)", fontSize: "var(--type-body-s)" }}
      >
        {q.error instanceof Error ? q.error.message : "Couldn't load meals."}
      </p>
    );
  }

  const meals = q.data ?? [];
  if (meals.length === 0) {
    return (
      <EmptyState
        icon={<Apple size={24} strokeWidth={1.5} />}
        headline="Nothing logged today."
        sub="Tap the meal button above to log your first one."
      />
    );
  }

  const totalKcal = meals.reduce((acc, m) => acc + (m.kcal ?? 0), 0);
  const totalP = meals.reduce(
    (acc, m) => acc + Number(m.macros?.p ?? 0),
    0,
  );
  const totalC = meals.reduce(
    (acc, m) => acc + Number(m.macros?.c ?? 0),
    0,
  );
  const totalF = meals.reduce(
    (acc, m) => acc + Number(m.macros?.f ?? 0),
    0,
  );

  return (
    <section aria-label="Today's meals">
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: "var(--space-3)",
        }}
      >
        <h3
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 500,
            fontSize: "var(--type-body-l)",
            margin: 0,
            color: "var(--fg)",
          }}
        >
          Today's meals
        </h3>
        <p
          className="mono"
          style={{
            margin: 0,
            color: "var(--fg-2)",
            fontSize: "var(--type-body-s)",
          }}
        >
          <UnitsRender q={{ kind: "kcal", kcal: totalKcal }} />{" "}
          <span style={{ color: "var(--fg-3)" }}>·</span> P{" "}
          {Math.round(totalP)}g <span style={{ color: "var(--fg-3)" }}>·</span> C{" "}
          {Math.round(totalC)}g <span style={{ color: "var(--fg-3)" }}>·</span> F{" "}
          {Math.round(totalF)}g
        </p>
      </header>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {meals.map((m) => (
          <MealRowItem key={m.id} m={m} onEdit={() => setEditingId(m.id)} />
        ))}
      </ul>
      <EditMealDialog
        mealId={editingId}
        open={editingId !== null}
        onClose={() => setEditingId(null)}
      />
    </section>
  );
}

function MealRowItem({ m, onEdit }: { m: MealRow; onEdit: () => void }) {
  const date = new Date(m.ts);
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return (
    <li>
      <button
        type="button"
        onClick={onEdit}
        aria-label={`Edit ${m.name || "meal"}`}
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "max-content 1fr max-content max-content",
          gap: "var(--space-3)",
          alignItems: "baseline",
          padding: "var(--space-3) 0",
          borderBottom: "1px solid var(--hair)",
          background: "transparent",
          border: "none",
          borderBottomColor: "var(--hair)",
          borderBottomStyle: "solid",
          borderBottomWidth: 1,
          color: "var(--fg)",
          textAlign: "left",
          cursor: "pointer",
          font: "inherit",
        }}
      >
        <span
          className="mono"
          style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}
        >
          {time}
        </span>
        <span style={{ color: "var(--fg)", fontSize: "var(--type-body)" }}>
          {m.name || "Meal"}
        </span>
        <span
          className="mono"
          style={{ color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}
        >
          <UnitsRender q={{ kind: "kcal", kcal: m.kcal ?? 0 }} />
        </span>
        <Pencil size={12} strokeWidth={1.5} color="var(--fg-3)" />
      </button>
    </li>
  );
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function todayBounds(): { startISO: string; endISO: string } {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { startISO: start.toISOString(), endISO: end.toISOString() };
}
